import { Card } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslate } from 'react-admin';
import type { AdminApiCredential, AdminApiCredentialListFilters } from '@/api/models';
import { AccessDenied } from '@/components/shell/AccessDenied';
import { CustomDataGrid, type DataGridColumn } from '@/components/shell/CustomDataGrid';
import { EmptyState } from '@/components/shell/EmptyState';
import { ErrorState } from '@/components/shell/ErrorState';
import { LoadingState } from '@/components/shell/LoadingState';
import { StatusChip } from '@/components/shell/StatusChip';
import { payApiClient } from '@/api/client';
import { useApiResource } from '@/hooks/useApiResource';
import { credentialStatusTone } from './domain';

interface CredentialsListViewProps {
  filters: AdminApiCredentialListFilters;
  onFiltersChange: (patch: Partial<AdminApiCredentialListFilters>) => void;
}

export function CredentialsListView({ filters, onFiltersChange }: CredentialsListViewProps) {
  const translate = useTranslate();
  const navigate = useNavigate();
  const { data, loading, error, reload } = useApiResource(
    () => payApiClient.listAdminApiCredentials(filters),
    JSON.stringify(filters),
  );

  if (loading) {
    return (
      <Card>
        <LoadingState variant="list" rows={8} />
      </Card>
    );
  }

  if (error) {
    if (error.errorCode === 'access_denied') {
      return (
        <Card>
          <AccessDenied />
        </Card>
      );
    }
    return (
      <Card>
        <ErrorState title={translate('pay.credentials.list.errorTitle')} body={error.message} onRetry={reload} />
      </Card>
    );
  }

  if (!data || data.items.length === 0) {
    return (
      <Card>
        <EmptyState title={translate('pay.credentials.list.emptyTitle')} body={translate('pay.credentials.list.emptyBody')} />
      </Card>
    );
  }

  const columns: Array<DataGridColumn<AdminApiCredential>> = [
    {
      key: 'application',
      label: translate('pay.credentials.list.columnApplication'),
      render: (item) => item.applicationCode,
    },
    {
      key: 'keyId',
      label: translate('pay.credentials.list.columnKeyId'),
      mono: 'code',
      render: (item) => item.keyId,
    },
    {
      key: 'status',
      mobileVisible: true,
      label: translate('pay.credentials.list.columnStatus'),
      render: (item) => (
        <StatusChip label={translate(`pay.credentials.status.${item.status}`)} tone={credentialStatusTone(item.status)} />
      ),
    },
    {
      key: 'scope',
      label: translate('pay.credentials.list.columnScope'),
      render: (item) =>
        item.allowAllTenants
          ? translate('pay.credentials.scope.allTenants')
          : `${translate('pay.credentials.scope.specificTenants')} (${item.allowedTenantCodes.length})`,
    },
    {
      key: 'rotatedAt',
      label: translate('pay.credentials.list.columnRotatedAt'),
      render: (item) =>
        item.rotatedAt ? new Date(item.rotatedAt).toLocaleString('es-ES') : translate('pay.credentials.list.neverRotated'),
    },
    {
      key: 'createdAt',
      label: translate('pay.credentials.list.columnCreatedAt'),
      render: (item) => new Date(item.createdAt).toLocaleString('es-ES'),
    },
  ];

  return (
    <CustomDataGrid
      columns={columns}
      rows={data.items}
      getRowKey={(item) => item.id}
      onRowClick={(item) => navigate(`/credentials/${encodeURIComponent(item.id)}`)}
      rowAriaLabel={translate('pay.credentials.list.viewDetail')}
      pagination={{
        page: data.page.page,
        pageSize: data.page.pageSize,
        totalItems: data.page.totalItems,
        rowsPerPageLabel: translate('pay.credentials.list.rowsPerPage'),
        onPageChange: (page) => onFiltersChange({ page }),
        onPageSizeChange: (pageSize) => onFiltersChange({ pageSize, page: 1 }),
      }}
    />
  );
}
